import { useEffect, useState } from 'react';
import { Alert, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { trpc } from '../lib/trpc';
import { formatCurrency } from '../lib/utils';
import { Button } from './Button';
import { CurrencyInput } from './CurrencyInput';
import { FormErrorBanner } from './FormErrorBanner';
import { ResponsiveModal } from './ResponsiveModal';
import { TextInput } from './TextInput';

export type MortgagePlanningReviewLiability = {
  id: string;
  accountName: string;
  institutionName: string | null;
  accountMask: string | null;
  liabilityType: 'mortgage' | 'student';
  currentBalance: number | null;
  nextPaymentAmount: number | null;
  principalAndInterestMonthly: number | null;
  propertyTaxMonthly: number | null;
  homeInsuranceMonthly: number | null;
  hoaMonthly: number | null;
};

type Draft = {
  principalAndInterestMonthly: string;
  propertyTaxMonthly: string;
  homeInsuranceMonthly: string;
  hoaMonthly: string;
};

type MortgagePlanningReviewSheetProps = {
  visible: boolean;
  liabilities: MortgagePlanningReviewLiability[];
  onClose: () => void;
  onSaved: (liabilityId: string) => void | Promise<void>;
};

function formatDraftNumber(value: number | null | undefined): string {
  if (value == null || value <= 0) return '';
  return String(Math.round(value * 100) / 100);
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function buildDraft(liability: MortgagePlanningReviewLiability): Draft {
  return {
    principalAndInterestMonthly: formatDraftNumber(liability.principalAndInterestMonthly),
    propertyTaxMonthly: formatDraftNumber(liability.propertyTaxMonthly),
    homeInsuranceMonthly: formatDraftNumber(liability.homeInsuranceMonthly),
    hoaMonthly: formatDraftNumber(liability.hoaMonthly),
  };
}

export function mortgageNeedsPlanningReview(
  liability: Pick<
    MortgagePlanningReviewLiability,
    | 'liabilityType'
    | 'principalAndInterestMonthly'
    | 'propertyTaxMonthly'
    | 'homeInsuranceMonthly'
    | 'hoaMonthly'
  >,
) {
  return (
    liability.liabilityType === 'mortgage' &&
    !((liability.principalAndInterestMonthly ?? 0) > 0) &&
    !((liability.propertyTaxMonthly ?? 0) > 0) &&
    !((liability.homeInsuranceMonthly ?? 0) > 0) &&
    !((liability.hoaMonthly ?? 0) > 0)
  );
}

export function MortgagePlanningReviewSheet({
  visible,
  liabilities,
  onClose,
  onSaved,
}: MortgagePlanningReviewSheetProps) {
  const { height: windowHeight } = useWindowDimensions();
  const updateMutation = trpc.liabilities.upsertPlanning.useMutation();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [error, setError] = useState<string | null>(null);
  const scrollMaxHeight = Math.min(windowHeight * 0.62, 560);

  useEffect(() => {
    if (!visible) return;
    setError(null);
    setDrafts(
      Object.fromEntries(
        liabilities.map((liability) => [liability.id, buildDraft(liability)]),
      ),
    );
  }, [liabilities, visible]);

  const updateDraft = (liabilityId: string, patch: Partial<Draft>) => {
    setDrafts((current) => {
      const source = liabilities.find((l) => l.id === liabilityId);
      if (!current[liabilityId] && !source) return current;
      return {
        ...current,
        [liabilityId]: {
          ...(current[liabilityId] ?? buildDraft(source!)),
          ...patch,
        },
      };
    });
    setError(null);
  };

  const saveLiability = async (liability: MortgagePlanningReviewLiability) => {
    const draft = drafts[liability.id];
    if (!draft) return;

    const principalAndInterestMonthly = parseOptionalNumber(draft.principalAndInterestMonthly);
    const propertyTaxMonthly = parseOptionalNumber(draft.propertyTaxMonthly);
    const homeInsuranceMonthly = parseOptionalNumber(draft.homeInsuranceMonthly);
    const hoaMonthly = parseOptionalNumber(draft.hoaMonthly);

    const invalidValues = [
      principalAndInterestMonthly,
      propertyTaxMonthly,
      homeInsuranceMonthly,
      hoaMonthly,
    ].some((value) => value != null && (!Number.isFinite(value) || value < 0));

    if (invalidValues) {
      setError('Enter valid non-negative mortgage amounts.');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        liabilityId: liability.id,
        principalAndInterestMonthly,
        propertyTaxMonthly,
        homeInsuranceMonthly,
        hoaMonthly,
      });
      await onSaved(liability.id);
    } catch {
      Alert.alert('Error', 'Could not save the mortgage planning details.');
    }
  };

  return (
    <ResponsiveModal
      visible={visible && liabilities.length > 0}
      onClose={onClose}
      shellClassName="overflow-hidden"
      header={
        <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Review mortgage payment
        </Text>
      }
      subtitle={
        <Text className="text-xs text-gray-500 dark:text-gray-400">
          Split your mortgage payment so FIRE planning can stop P&I after payoff while keeping escrow costs.
        </Text>
      }
    >
      <ScrollView
        className="px-5"
        style={{ maxHeight: scrollMaxHeight }}
        contentContainerStyle={{ paddingBottom: 16, gap: 16 }}
        keyboardShouldPersistTaps="handled"
      >
        <FormErrorBanner error={error} onDismiss={() => setError(null)} />
        {liabilities.map((liability) => {
          const draft = drafts[liability.id] ?? buildDraft(liability);
          return (
            <View
              key={liability.id}
              className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 gap-3"
            >
              <View className="gap-1">
                <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {liability.accountName}
                </Text>
                <Text className="text-xs text-gray-500 dark:text-gray-400">
                  {[
                    liability.institutionName,
                    liability.accountMask ? `••••${liability.accountMask}` : null,
                    liability.currentBalance != null
                      ? `${formatCurrency(liability.currentBalance, { decimals: 0 })} balance`
                      : null,
                    liability.nextPaymentAmount != null
                      ? `${formatCurrency(liability.nextPaymentAmount, { decimals: 0 })}/mo payment`
                      : null,
                  ].filter(Boolean).join(' · ')}
                </Text>
              </View>

              <CurrencyInput
                label="Principal & interest monthly"
                value={draft.principalAndInterestMonthly}
                onChangeText={(principalAndInterestMonthly) =>
                  updateDraft(liability.id, { principalAndInterestMonthly })
                }
                placeholder={
                  liability.nextPaymentAmount != null
                    ? String(Math.round(liability.nextPaymentAmount * 100) / 100)
                    : '0.00'
                }
                size="sm"
              />
              <CurrencyInput
                label="Property tax monthly"
                value={draft.propertyTaxMonthly}
                onChangeText={(propertyTaxMonthly) =>
                  updateDraft(liability.id, { propertyTaxMonthly })
                }
                placeholder="0.00"
                size="sm"
              />
              <CurrencyInput
                label="Home insurance monthly"
                value={draft.homeInsuranceMonthly}
                onChangeText={(homeInsuranceMonthly) =>
                  updateDraft(liability.id, { homeInsuranceMonthly })
                }
                placeholder="0.00"
                size="sm"
              />
              <CurrencyInput
                label="HOA monthly"
                value={draft.hoaMonthly}
                onChangeText={(hoaMonthly) => updateDraft(liability.id, { hoaMonthly })}
                placeholder="0.00"
                size="sm"
              />
              <Button
                size="sm"
                onPress={() => {
                  void saveLiability(liability);
                }}
                loading={updateMutation.isPending}
              >
                Save mortgage details
              </Button>
            </View>
          );
        })}
      </ScrollView>
      <View className="border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 px-5 py-4">
        <Button variant="secondary" onPress={onClose} disabled={updateMutation.isPending}>
          Skip for now
        </Button>
      </View>
    </ResponsiveModal>
  );
}
